import { useEffect, type RefObject } from 'react';
import { SurveyV2 } from '@act/shared/data-transfer-objects';
import { useToast } from '@act/react-components/app/ReusableToast';
import { surveysRequests } from 'redux/requests/surveys.requests';
import { surveyActions } from 'redux/slices/surveys.slice';
import { useAppDispatch } from 'redux/storeHooks';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { flushPendingNoteAutosave } from '../flushPendingNoteAutosave.util';
import type { NoteFieldRef } from '../NoteField';
import { useAssignedSurveyNoteSaveOverride } from './useAssignedSurveyNoteSaveOverride.hook';

/** Params for {@link useAssignedSurveySaveActions}. */
export interface UseAssignedSurveySaveActionsParams {
  assignedSurveyId: number;
  assignedSurvey: SurveyV2.AssignedSurveyDto | undefined;
  isReviewMode: boolean;
  noteFieldRef: RefObject<NoteFieldRef | null>;
  getNotes: () => string;
  /** Baseline notes string used to detect changes (empty vs empty-editor HTML). */
  notesBaseline: string;
  onClose: () => void;
  /** Optional hook run before closing (e.g. reset RHF state). */
  onBeforeClose?: () => void;
}

/**
 * Shared save/close flow for assigned-survey surfaces (dialog and drawer).
 * Flushes note autosave, optionally submits the survey, and syncs Redux via the note hook.
 *
 * @param params - Survey context, form accessors, and close callback.
 * @returns Note override, save handler, and submit loading state.
 */
export function useAssignedSurveySaveActions({
  assignedSurveyId,
  assignedSurvey,
  isReviewMode,
  noteFieldRef,
  getNotes,
  notesBaseline,
  onClose,
  onBeforeClose,
}: UseAssignedSurveySaveActionsParams) {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const [submittedSurvey, isSubmittingSurvey, submitSurvey] = useApiCall(
    surveysRequests.submitAssignedSurvey
  );

  const { assignedSurveyNoteSaveOverride, isNoteAutosaveErrored } =
    useAssignedSurveyNoteSaveOverride({
      assignedSurveyId,
    });

  useEffect(() => {
    if (submittedSurvey) {
      dispatch(surveyActions.upsertAssignedSurvey(submittedSurvey));
      onBeforeClose?.();
      onClose();
      toast('Successfully saved survey');
    }
  }, [submittedSurvey]);

  /**
   * Flushes notes, submits the survey when required, or closes when only notes changed.
   */
  async function saveAndClose(): Promise<void> {
    const notes = getNotes();
    const notesChanged = notes !== notesBaseline;
    const shouldSubmitSurvey = !isReviewMode && !assignedSurvey?.completedAt;
    const hasChanges = notesChanged || shouldSubmitSurvey;

    if (!hasChanges) {
      onBeforeClose?.();
      onClose();
      return;
    }

    const noteReady = await flushPendingNoteAutosave(noteFieldRef);
    if (!noteReady) {
      return;
    }

    if (!shouldSubmitSurvey) {
      onBeforeClose?.();
      onClose();
      if (notesChanged) {
        toast('Successfully updated survey');
      }
      return;
    }

    submitSurvey({
      params: { assignedSurveyId },
      body: null,
    });
  }

  return {
    assignedSurveyNoteSaveOverride,
    saveAndClose,
    submittingForm: isSubmittingSurvey === true,
    isSaveDisabled: isNoteAutosaveErrored,
  };
}
